import { useRef, useState, useCallback, useEffect } from 'react';

const TRACKS = {
  lobby: '/audio/lobby-music.mp3',
  countdown: '/audio/countdown-music.mp3',
  podium: '/audio/podium-music.mp3',
};

/**
 * Custom hook for managing background music across the quiz session.
 * Handles play, stop, fade out, and mute/unmute for lobby, countdown, and podium tracks.
 */
export default function useAudioManager() {
  const audioRefs = useRef({});
  const fadeIntervalRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const volumeRef = useRef(0.5);

  // Lazily create Audio elements on first use
  const getAudio = useCallback((trackName) => {
    if (!audioRefs.current[trackName]) {
      const audio = new Audio(TRACKS[trackName]);
      audio.preload = 'auto';
      audioRefs.current[trackName] = audio;
    }
    return audioRefs.current[trackName];
  }, []);

  // Stop all tracks immediately
  const stopAll = useCallback(() => {
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
    Object.values(audioRefs.current).forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    setCurrentTrack(null);
  }, []);

  // Play a specific track
  const play = useCallback((trackName, { loop = false, volume = 0.5, startOffset = 0 } = {}) => {
    stopAll();
    const audio = getAudio(trackName);
    audio.loop = loop;
    volumeRef.current = volume;
    audio.volume = isMuted ? 0 : volume;
    audio.currentTime = startOffset;
    audio.play().catch(() => {
      // Browser autoplay policy - silently handle
    });
    setCurrentTrack(trackName);
  }, [stopAll, getAudio, isMuted]);

  // Fade out current track over durationMs, then call onComplete
  const fadeOut = useCallback((durationMs = 1000, onComplete) => {
    const activeTrack = currentTrack;
    const activeAudio = activeTrack ? audioRefs.current[activeTrack] : null;
    if (!activeAudio || activeAudio.paused) {
      onComplete?.();
      return;
    }

    const steps = 20;
    const stepMs = durationMs / steps;
    const volumeStep = activeAudio.volume / steps;

    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
    }

    fadeIntervalRef.current = setInterval(() => {
      if (activeAudio.volume - volumeStep <= 0.01) {
        activeAudio.volume = 0;
        activeAudio.pause();
        activeAudio.currentTime = 0;
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
        setCurrentTrack(null);
        onComplete?.();
      } else {
        activeAudio.volume = Math.max(0, activeAudio.volume - volumeStep);
      }
    }, stepMs);
  }, [currentTrack]);

  // Toggle mute/unmute
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      Object.values(audioRefs.current).forEach((audio) => {
        if (!audio.paused) {
          audio.volume = newMuted ? 0 : volumeRef.current;
        }
      });
      return newMuted;
    });
  }, []);

  // Cleanup all audio on unmount
  useEffect(() => {
    const refs = audioRefs.current;
    return () => {
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      Object.values(refs).forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  return { play, stopAll, fadeOut, toggleMute, isMuted, currentTrack };
}
